/**
 * Auto-Counter-Statistik-Kachel (Folge-Feature).
 *
 * Kompakte Karte im Dealer-Dashboard, die die Wirkung der Auto-Counter-Funktion
 * misst: gesendete automatische Gegenvorschläge · angenommene · zusätzlicher
 * Umsatz (Summe der angenommenen Auto-Counter). Wird nur angezeigt, wenn der
 * Händler mindestens einen Auto-Counter gesendet hat (sonst kein Clutter).
 *
 * Datenquelle: ``GET /api/dealer/auto-counter-stats``.
 */
import { useEffect, useState } from "react";
import { Zap, CheckCircle2, Euro } from "lucide-react";
import api from "../../lib/api";

const fmtEur = (n) =>
    new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

export default function AutoCounterStatsCard({ user }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!user || user.role !== "b2b") return;
        api.get("/dealer/auto-counter-stats")
            .then((r) => setData(r.data))
            .catch(() => setData(null));
    }, [user]);

    if (!user || user.role !== "b2b") return null;
    if (!data || (data.sent || 0) === 0) return null;

    const acceptRate = data.sent ? Math.round((data.accepted / data.sent) * 100) : 0;

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid="auto-counter-stats-card">
            <div className="flex items-center gap-1.5 mb-2">
                <Zap className="w-3.5 h-3.5 text-[#FFCC00]" />
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-600">
                    Auto-Gegenvorschläge
                </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
                <div className="bg-[#FFCC00]/10 border border-[#FFCC00]/40 rounded-sm px-2 py-1.5 text-center" data-testid="auto-counter-sent">
                    <div className="text-[9px] uppercase font-bold tracking-wider text-gray-600 flex items-center justify-center gap-1">
                        <Zap className="w-2.5 h-2.5" /> Gesendet
                    </div>
                    <div className="font-display font-black text-base font-mono-num leading-tight">{data.sent}</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1.5 text-center" data-testid="auto-counter-accepted">
                    <div className="text-[9px] uppercase font-bold tracking-wider text-gray-600 flex items-center justify-center gap-1">
                        <CheckCircle2 className="w-2.5 h-2.5" /> Angenommen
                    </div>
                    <div className="font-display font-black text-base font-mono-num leading-tight">{data.accepted}</div>
                    <div className="text-[8px] text-gray-500">{acceptRate}% Quote</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-sm px-2 py-1.5 text-center" data-testid="auto-counter-revenue">
                    <div className="text-[9px] uppercase font-bold tracking-wider text-gray-600 flex items-center justify-center gap-1">
                        <Euro className="w-2.5 h-2.5" /> Umsatz
                    </div>
                    <div className="font-display font-black text-sm font-mono-num leading-tight" data-testid="auto-counter-revenue-value">
                        {fmtEur(data.additional_revenue)}
                    </div>
                    <div className="text-[8px] text-gray-500">zusätzlich</div>
                </div>
            </div>
            {data.open > 0 && (
                <div className="text-[10px] text-gray-500 mt-2" data-testid="auto-counter-open">
                    {data.open} offen — warten auf Käufer-Annahme
                </div>
            )}
        </div>
    );
}
